/**
 * V2 Personal category page — /adventure/user/{username}/category/{slug}
 * (docs/JOURNAL-PLAN.md §11).
 *
 * One explorer's own shelf, readable by anyone, wearing the SAME shape
 * as the profile's drilled-in category view (user ask 2026-08-11): a
 * collage hero from its yoinks, the shelf's name with the owner chip
 * beside it, and the handwritten story — "Edit story" for the owner,
 * exactly like the curated category pages. The story lives on
 * personal_categories.description (20260811e): a shelf has exactly one
 * owner, so no per-user story table is needed.
 *
 * The yoinks on it are regular yoinks (standard cards, own pages); the
 * shelf just keeps them off the public feeds, search, map pins and
 * category counts. Owner tools: + Yoink (log form with this shelf
 * pre-chosen), rename/emoji, and delete — deleting unfiles the yoinks
 * (FK on delete set null), never deletes them.
 */
import { escapeHtml } from '../../../assets/js/utils.js';
import { yoinkCard, thumbUrl, heroPhotos } from '../../ui/card.js';
import { showToast } from '../../ui/toast.js';
import { shareButton, handleShareClick, setUrlParam, getUrlParam } from '../../ui/share.js';
import { shareMenu } from '../../ui/share-menu.js';
import { embedButton, handleEmbedClick, isEmbed } from '../../ui/embed.js';
import { mountMap, mapsAvailable } from '../../ui/map-view.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';
import { avatarHtml } from '../profile/profile-derive.js';
import {
  fetchPersonalCategoryPage, updatePersonalCategory, deletePersonalCategory,
} from './journal.js';

export function mount(el, ctx) {
  const { store, router, params } = ctx;
  let destroyed = false;
  let data = null; // { owner, shelf, yoinks }
  // The story block, the §10-profile pattern: draft buffers typed text
  // through async re-renders; never a full render from the input handler
  let storyEditing = false;
  let storySaving = false;
  let storyError = '';
  let storyDraft = null;
  // Rename/emoji editor + the delete confirm, owner only
  let managing = false;
  let manageBusy = false;
  let confirmingDelete = false;
  let manageError = '';

  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  const embedded = isEmbed();
  // Grid/Map, the journal's own toggle (2026-09-15): a shelf of places
  // is a map as much as the rest of the journal is, and without one there
  // was nothing for an embed to frame. ?view= keeps a shared link honest;
  // otherwise the session's choice follows you here like everywhere else.
  let viewMode = getUrlParam('view') === 'map' ? 'map'
    : getUrlParam('view') === 'grid' ? 'grid'
    : (embedded || store.get().viewMode === 'map') ? 'map' : 'grid';
  let mapHandle = null;
  let mapFitDone = false; // frame the shelf on first open; keep your zoom after that
  let embedShellBuilt = false; // ?embed=1 builds its (billed) map once

  function isOwner() {
    return !!data && store.get().user?.id === data.owner.id;
  }

  /**
   * Anything to map? A shelf of yoinks with no located places would
   * embed as an empty frame on someone else's site, so both the Map
   * toggle and the Embed button wait for a first pin.
   */
  function hasPins() {
    return !!data?.yoinks?.some((y) => y.location_id);
  }

  function collageHtml() {
    const photos = heroPhotos(data.yoinks || []);
    if (photos.length < 3) return '';
    return `
      <div class="cat-hero-collage${photos.length >= 5 ? ' collage-mosaic' : ''}" aria-hidden="true">
        ${photos.slice(0, 5).map((url, i) => `<img src="${thumbUrl(url)}" alt="" loading="${i < 3 ? 'eager' : 'lazy'}"
          data-fallback-src="${url}">`).join('')}
      </div>`;
  }

  /** The story, view or edit — the profile's collection-story block. */
  function storyBlockHtml(own) {
    const story = (data.shelf.description || '').trim();
    if (own && storyEditing) {
      return `
        <div class="collection-story-edit">
          <textarea data-story-input rows="4" maxlength="1000"
            aria-label="The story behind your ${escapeHtml(data.shelf.name)}"
            placeholder="What makes this one special to you?">${escapeHtml(storyDraft !== null ? storyDraft : story)}</textarea>
          ${storyError ? `<div class="v2-form-error" role="alert">${escapeHtml(storyError)}</div>` : ''}
          <div class="collection-story-actions">
            <button type="button" class="v2-btn-more" data-story-save ${storySaving ? 'disabled' : ''}>
              ${storySaving ? 'Saving…' : 'Save story'}</button>
            <button type="button" class="v2-btn-more" data-story-cancel ${storySaving ? 'disabled' : ''}>Cancel</button>
          </div>
        </div>`;
    }
    if (story) {
      return `
        <div class="collection-story">
          <p class="collection-story-text">${escapeHtml(story)}</p>
          ${own ? '<button type="button" class="v2-btn-more" data-story-edit>Edit story</button>' : ''}
        </div>`;
    }
    return own
      ? `<button type="button" class="v2-btn-more collection-story-add" data-story-edit>
          ✎ Tell the story behind your ${escapeHtml(data.shelf.name)}</button>`
      : '';
  }

  /** Owner tools: + Yoink, rename/emoji, delete (with its confirm). */
  function manageHtml() {
    if (managing) {
      return `
        <div class="collection-story-edit pc-manage-edit">
          <label class="v2-field">Name
            <input type="text" data-shelf-name maxlength="80" value="${escapeHtml(data.shelf.name)}">
          </label>
          <label class="v2-field">Emoji (optional)
            <input type="text" data-shelf-emoji maxlength="4" value="${escapeHtml(data.shelf.emoji || '')}" placeholder="🔒">
          </label>
          <p class="log-hint">The link stays the same — only the name changes.</p>
          ${manageError ? `<div class="v2-form-error" role="alert">${escapeHtml(manageError)}</div>` : ''}
          <div class="collection-story-actions">
            <button type="button" class="v2-btn-more" data-shelf-save ${manageBusy ? 'disabled' : ''}>
              ${manageBusy ? 'Saving…' : 'Save'}</button>
            <button type="button" class="v2-btn-more" data-shelf-manage-cancel ${manageBusy ? 'disabled' : ''}>Cancel</button>
          </div>
        </div>`;
    }
    if (confirmingDelete) {
      const n = data.yoinks.length;
      return `
        <div class="collection-story-edit pc-manage-edit">
          <p class="log-hint">Delete “${escapeHtml(data.shelf.name)}”? Its ${n} yoink${n === 1 ? '' : 's'} stay${n === 1 ? 's' : ''}
            in your journal — just uncategorized until you refile them.</p>
          ${manageError ? `<div class="v2-form-error" role="alert">${escapeHtml(manageError)}</div>` : ''}
          <div class="collection-story-actions">
            <button type="button" class="v2-btn-danger" data-shelf-delete-confirm ${manageBusy ? 'disabled' : ''}>
              ${manageBusy ? 'Deleting…' : 'Delete category'}</button>
            <button type="button" class="v2-btn-more" data-shelf-manage-cancel ${manageBusy ? 'disabled' : ''}>Cancel</button>
          </div>
        </div>`;
    }
    return `
      <div class="collection-story-actions pc-manage-row">
        <button type="button" class="v2-btn-more" data-pc-new-yoink>+ Yoink onto this shelf</button>
        <button type="button" class="v2-btn-more" data-shelf-manage>Rename</button>
        <button type="button" class="v2-btn-more" data-shelf-delete>Delete</button>
      </div>`;
  }

  function render() {
    if (!data) {
      el.innerHTML = `
        <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
        <div class="v2-empty">Nothing here.</div>`;
      return;
    }
    const { owner, shelf, yoinks } = data;
    const own = isOwner();
    const who = owner.full_name || owner.username;
    document.title = `${shelf.name} | Yoink Adventures v2`;
    // The embed shell owns its map for the life of the frame — tearing it
    // down on a re-render would blank a map a reader is already looking at
    if (embedded) { renderEmbed(); return; }
    mapHandle?.cleanup();
    mapHandle = null;
    if (!own) setRibbonContext(`${who} keeps their own categories of places. Start yours.`);
    const collage = collageHtml();
    const places = new Set(yoinks.map((y) => y.location_id).filter(Boolean)).size;
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back">
        <a href="${basePath}/user/${encodeURIComponent(owner.username)}" data-back-owner>← ${escapeHtml(own ? 'My journal' : `${who}’s journal`)}</a>
        <span class="yoink-share-row">
          ${shareMenu(
            hasPins() ? embedButton(`${shelf.name} — ${who}`) : '',
            shareButton(`${shelf.name} — ${who} on Yoink Adventures`)
          )}
        </span>
      </nav>
      <header class="cat-hero journal-focus-hero pc-page">
        ${collage}
        <div class="cat-hero-panel${collage ? ' has-collage' : ''}">
          <div class="journal-cat-head">
            <h2 class="journal-cat-title">
              <span aria-hidden="true">${shelf.emoji ? escapeHtml(shelf.emoji) : '🔒'}</span>
              <span>${escapeHtml(shelf.name)}</span>
            </h2>
            <button type="button" class="journal-cat-owner" data-back-owner
              aria-label="Back to ${own ? 'your' : `${escapeHtml(who)}’s`} journal">
              ${avatarHtml(owner)}
              <span class="journal-cat-owner-name">${escapeHtml(who)}</span>
              <span class="journal-cat-owner-count">${yoinks.length} yoink${yoinks.length === 1 ? '' : 's'}${
                places && places !== yoinks.length ? ` · ${places} place${places === 1 ? '' : 's'}` : ''}</span>
            </button>
          </div>
          <p class="log-hint">${own ? 'Your personal category' : `A personal category by ${escapeHtml(who)}`} ·
            off the public map &amp; feeds</p>
          ${storyBlockHtml(own)}
          ${own ? manageHtml() : ''}
        </div>
      </header>
      ${hasPins() ? `
      <div class="v2-toggles">
        <div class="v2-scope" role="group" aria-label="View">
          <button class="v2-scope-btn ${viewMode === 'grid' ? 'scope-active' : ''}"
            data-pc-view="grid" aria-pressed="${viewMode === 'grid'}">Grid</button>
          <button class="v2-scope-btn ${viewMode === 'map' ? 'scope-active' : ''}"
            data-pc-view="map" aria-pressed="${viewMode === 'map'}">Map</button>
        </div>
      </div>` : ''}
      ${viewMode === 'map' && hasPins()
    ? '<div class="v2-map" data-pc-map></div>'
    : yoinks.length
      ? `<div class="v2-grid">${yoinks.map(yoinkCard).join('')}</div>`
      : `<div class="v2-empty">${own ? 'Nothing filed here yet — log a yoink and pick this category.' : 'Nothing here yet.'}</div>`}`;
    if (viewMode === 'map' && hasPins()) {
      // Framed to the shelf on the FIRST open only — toggling back to the
      // grid and out again keeps the zoom you left it at (profile.js).
      mapHandle = mountMap(el.querySelector('[data-pc-map]'), {
        store, router, fitToMarkers: !mapFitDone,
      });
      if (mapsAvailable()) {
        mapHandle.setYoinks(yoinks);
        mapFitDone = true;
      }
    }
  }

  /**
   * The frame on someone else's page: an identity strip and the map,
   * nothing else — the category page's embed shape and CSS
   * (features/category buildShell). Built once: render() runs again on
   * store noise, and a remount would construct a second (billed) Google
   * map on a stranger's site.
   */
  function renderEmbed() {
    const { owner, shelf, yoinks } = data;
    const who = owner.full_name || owner.username;
    const title = `${shelf.name} · ${who}`;
    if (!embedShellBuilt) {
      embedShellBuilt = true;
      el.innerHTML = `
        <div class="cat-embed-head">
          <span class="cat-head-icon" aria-hidden="true">${shelf.emoji ? escapeHtml(shelf.emoji) : '🗂️'}</span>
          <strong>${escapeHtml(title)}</strong>
          <span class="cat-embed-count" data-embed-count aria-live="polite"></span>
        </div>
        <section class="cat-map-splash cat-embed-splash" aria-label="${escapeHtml(title)} map">
          <div class="v2-map cat-splash-map" data-pc-map></div>
        </section>`;
      mapHandle = mountMap(el.querySelector('[data-pc-map]'), {
        store, router, fitToMarkers: true,
      });
    }
    const count = el.querySelector('[data-embed-count]');
    if (count) count.textContent = yoinks.length ? `${yoinks.length} yoink${yoinks.length === 1 ? '' : 's'}` : '';
    if (mapsAvailable()) mapHandle?.setYoinks(yoinks);
  }

  async function onStorySave() {
    const input = el.querySelector('[data-story-input]');
    if (!input || storySaving) return;
    storySaving = true;
    storyError = '';
    storyDraft = input.value;
    render();
    try {
      const text = storyDraft.trim();
      await updatePersonalCategory(data.shelf.id, { description: text || null });
      if (destroyed) return;
      data.shelf.description = text;
      storyEditing = false;
      storyDraft = null;
    } catch {
      storyError = 'Couldn’t save the story. Please try again.';
    }
    storySaving = false;
    if (!destroyed) render();
  }

  async function onShelfSave() {
    if (manageBusy) return;
    const name = el.querySelector('[data-shelf-name]')?.value.trim();
    const emoji = el.querySelector('[data-shelf-emoji]')?.value.trim();
    if (!name) {
      manageError = 'Give it a name.';
      return render();
    }
    manageBusy = true;
    manageError = '';
    render();
    try {
      await updatePersonalCategory(data.shelf.id, { name, emoji: emoji || null });
      if (destroyed) return;
      data.shelf.name = name;
      data.shelf.emoji = emoji || null;
      managing = false;
      showToast('Category updated.', { type: 'success' });
    } catch {
      manageError = 'Couldn’t save — try again.';
    }
    manageBusy = false;
    if (!destroyed) render();
  }

  async function onShelfDelete() {
    if (manageBusy) return;
    manageBusy = true;
    manageError = '';
    render();
    try {
      await deletePersonalCategory(data.shelf.id);
      if (destroyed) return;
      showToast(`“${data.shelf.name}” deleted — its yoinks are in your journal, uncategorized.`,
        { type: 'success' });
      router.navigate('user', { username: data.owner.username });
      return;
    } catch {
      manageError = 'Couldn’t delete — try again.';
    }
    manageBusy = false;
    if (!destroyed) render();
  }

  function onClick(event) {
    if (handleShareClick(event)) return;
    if (handleEmbedClick(event)) return;
    const view = event.target.closest('[data-pc-view]');
    if (view) {
      if (view.dataset.pcView !== viewMode) {
        viewMode = view.dataset.pcView;
        setUrlParam('view', viewMode === 'map' ? 'map' : '');
        store.set({ viewMode }); // the mode follows you to other pages (V1)
        render();
      }
      return;
    }
    const back = event.target.closest('[data-back-owner]');
    if (back) {
      event.preventDefault();
      router.navigate('user', { username: data.owner.username });
      return;
    }
    if (event.target.closest('[data-pc-new-yoink]')) {
      router.navigate('log', {}, { query: { personal: data.shelf.id } });
      return;
    }
    if (event.target.closest('[data-story-edit]')) {
      storyEditing = true;
      storyError = '';
      storyDraft = null;
      render();
      el.querySelector('[data-story-input]')?.focus();
      return;
    }
    if (event.target.closest('[data-story-cancel]')) {
      storyEditing = false;
      storyError = '';
      storyDraft = null;
      render();
      return;
    }
    if (event.target.closest('[data-story-save]')) {
      onStorySave();
      return;
    }
    if (event.target.closest('[data-shelf-manage]')) {
      managing = true;
      confirmingDelete = false;
      manageError = '';
      render();
      el.querySelector('[data-shelf-name]')?.focus();
      return;
    }
    if (event.target.closest('[data-shelf-delete]')) {
      confirmingDelete = true;
      managing = false;
      manageError = '';
      render();
      return;
    }
    if (event.target.closest('[data-shelf-manage-cancel]')) {
      managing = false;
      confirmingDelete = false;
      manageError = '';
      render();
      return;
    }
    if (event.target.closest('[data-shelf-save]')) {
      onShelfSave();
      return;
    }
    if (event.target.closest('[data-shelf-delete-confirm]')) {
      onShelfDelete();
      return;
    }
    const personalRow = event.target.closest('[data-personal-slug]');
    if (personalRow) {
      router.navigate('personal-category', {
        username: personalRow.dataset.personalOwner, slug: personalRow.dataset.personalSlug,
      });
      return;
    }
    const locationRow = event.target.closest('[data-location-id]');
    if (locationRow) {
      router.navigate('location', { id: locationRow.dataset.locationId });
      return;
    }
    const userRow = event.target.closest('[data-username]');
    if (userRow && userRow.dataset.username) {
      router.navigate('user', { username: userRow.dataset.username });
      return;
    }
    const card = event.target.closest('[data-yoink-id]');
    if (card) router.navigate('yoink', { id: card.dataset.yoinkId });
  }

  function onInput(event) {
    // Buffer only — an async re-render mid-sentence must not reset the box
    const storyInput = event.target.closest('[data-story-input]');
    if (storyInput) storyDraft = storyInput.value;
  }

  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target.closest('[data-yoink-id], [data-location-id], [data-username], [data-personal-slug]');
    if (!target || target.tagName === 'BUTTON') return;
    event.preventDefault();
    onClick(event);
  }

  el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
  fetchPersonalCategoryPage(params.username, params.slug)
    .then((found) => {
      if (destroyed) return;
      data = found;
      render();
    })
    .catch(() => {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    });

  el.addEventListener('click', onClick);
  el.addEventListener('input', onInput);
  el.addEventListener('keydown', onKeydown);

  return () => {
    destroyed = true;
    mapHandle?.cleanup();
    mapHandle = null;
    el.removeEventListener('click', onClick);
    el.removeEventListener('input', onInput);
    el.removeEventListener('keydown', onKeydown);
  };
}
